import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { FormProvider, useForm } from 'react-hook-form';

import { getSelfScheduleCriteriaOptions, getSelfScheduleCriteriaQueryKey, updateSelfScheduleCriteriaMutation } from '@drivingdoc/api-client/generated/react-query';
import { type MinimalUser } from 'api';

import CheckBoxInput from 'components/FormInputs/CheckBoxInput.tsx';
import IntervalInput from 'components/FormInputs/IntervalInput';
import CurrencyInput from 'components/Forms/CurrencyInput.tsx';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';
import normalizePgInterval from 'utils/normalizePgInterval.ts';

import DateModified from '../../components/DateModified.tsx';
import DialogFormSection from '../../components/DialogFormSection.tsx';
import FeedbackDialog from '../../FeedbackDialog/index.ts';
import { FormInputValues, FormOutputValues, FormSchema } from './formSchema.ts';

dayjs.extend(calendar);

interface UpdateCourseSelfScheduleCriteriaDialogPayload {
  /**
   * The course id to update
   */
  courseId: string;
}

export function UpdateCourseSelfScheduleCriteriaDialog({
  payload,
  open,
  onClose,
}: DialogProps<UpdateCourseSelfScheduleCriteriaDialogPayload>) {
  const queryClient = useQueryClient();
  const dialogs = useDialogs();

  const courseSelfScheduleCriteria = useQuery({
    ...getSelfScheduleCriteriaOptions({
      path: {
        id: payload.courseId,
      },
    }),
    enabled: !!payload.courseId,
  });

  const updateUpdateCourseSelfScheduleCriteria = useMutation({ ...updateSelfScheduleCriteriaMutation() });

  const form = useForm<FormInputValues, unknown, FormOutputValues>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    values: {
      min_time_from_first_to_last: normalizePgInterval(courseSelfScheduleCriteria.data?.min_time_from_first_to_last),
      max_time_from_first_to_last: normalizePgInterval(courseSelfScheduleCriteria.data?.max_time_from_first_to_last),
      min_time_between_periods: normalizePgInterval(courseSelfScheduleCriteria.data?.min_time_between_periods),
      max_bookings_in_advance: courseSelfScheduleCriteria.data?.max_bookings_in_advance ?? 0,
      max_bookings_in_interval: courseSelfScheduleCriteria.data?.max_bookings_in_interval ?? 0,
      max_bookings_interval: normalizePgInterval(courseSelfScheduleCriteria.data?.max_bookings_interval),
      require_permit: courseSelfScheduleCriteria.data?.require_permit ?? false,
      first_period_max_balance: courseSelfScheduleCriteria.data?.first_period_max_balance ?? null,
      last_period_max_balance: courseSelfScheduleCriteria.data?.last_period_max_balance ?? null,
      other_max_balance: courseSelfScheduleCriteria.data?.other_max_balance ?? null,
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = form;

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (data: FormOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('This will affect all students enrolled in the course.', {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateUpdateCourseSelfScheduleCriteria.mutateAsync({
        path: {
          id: payload.courseId,
        },
        body: data,
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: getSelfScheduleCriteriaQueryKey({
            path: { id: payload.courseId },
          }),
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Requirements Updated',
        });
      }
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <DialogTitle>Update Student Scheduling Policy</DialogTitle>

      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogContent>
            <DialogFormSection title="Maximum Drives In Timeframe">
              <Grid size={4}>
                <TextField
                  label="Max # Of Drives"
                  variant="standard"
                  type="number"
                  fullWidth
                  slotProps={{
                    htmlInput: { min: 0 },
                    inputLabel: { shrink: true },
                  }}
                  error={!!errors.max_bookings_interval}
                  helperText={errors.max_bookings_interval?.message}
                  {...register('max_bookings_in_interval', { valueAsNumber: true })}
                />
              </Grid>
              <Grid size={8}>
                <IntervalInput name="max_bookings_interval" months weeks days />
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Max # Of Future Enrollments"
                  variant="standard"
                  type="number"
                  slotProps={{
                    htmlInput: { min: 0 },
                    inputLabel: { shrink: true },
                  }}
                  error={!!errors.max_bookings_in_advance}
                  helperText={errors.max_bookings_in_advance?.message}
                  {...register('max_bookings_in_advance', { valueAsNumber: true })}
                />
              </Grid>
              <Grid size={8}>
                <CheckBoxInput name="require_permit" label="Require Permit To Schedule" />
              </Grid>
            </DialogFormSection>
            <DialogFormSection title="Student Balance Restrictions">
              <Grid size={4}>
                <CurrencyInput
                  name="first_period_max_balance"
                  label="First Drive Max Balance"
                  allowNull
                  placeholder="No Restriction"
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  name="last_period_max_balance"
                  label="Last Drive Max Balance"
                  allowNull
                  placeholder="No Restriction"
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  name="other_max_balance"
                  label="Other Max Balance"
                  allowNull
                  placeholder="No Restriction"
                />
              </Grid>
            </DialogFormSection>

            <Box pt={2}>
              <DateModified
                prefix="Updated: "
                date={courseSelfScheduleCriteria.data?.updated_at}
                user={courseSelfScheduleCriteria.data?.updated_by_user as MinimalUser}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Update</Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={updateUpdateCourseSelfScheduleCriteria.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateCourseSelfScheduleCriteriaDialog;
